import React, { useState, useEffect } from 'react';
import { UserPlus, Search, Clock, Calendar, Phone, MapPin, FileText, CheckCircle, AlertCircle, Activity } from 'lucide-react';
import { useAlert } from './contexts/AlertContext';
import { useAuth } from './contexts/AuthContext';
import apiService from './apiService';
import PrintQueueTicket from './PrintQueueTicket';

// Module-level cache to persist state across component unmounts
let persistentCheckInState = {
    searchTerm: '',
    searchResults: [],
    selectedPatient: null
};

const ReceptionistCheckIn = ({ onNavigate }) => {
    const { currentUser } = useAuth();
    const { showAlert } = useAlert();
    
    // Initialize state from persistent cache
    const [searchTerm, setSearchTerm] = useState(persistentCheckInState.searchTerm);
    const [searchResults, setSearchResults] = useState(persistentCheckInState.searchResults);
    const [selectedPatient, setSelectedPatient] = useState(persistentCheckInState.selectedPatient);
    const [loading, setLoading] = useState(false);
    const [checkInData, setCheckInData] = useState({
        appointmentType: 'consultation',
        department: '',
        priority: 'medium',
        notes: '',
        symptoms: persistentCheckInState.selectedPatient?.symptoms || ''
    });
    const [vitals, setVitals] = useState({
        weight: '',
        bpSystolic: '',
        bpDiastolic: '',
        temperature: '',
        heartRate: '',
        height: ''
    });
    const [showVitalsAlert, setShowVitalsAlert] = useState(false);
    const [vitalsAnalysis, setVitalsAnalysis] = useState('');
    const [recentCheckIns, setRecentCheckIns] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [showPrintTicket, setShowPrintTicket] = useState(false);
    const [ticketData, setTicketData] = useState(null);
    
    // Update persistent cache whenever state changes
    useEffect(() => {
        persistentCheckInState = {
            searchTerm,
            searchResults,
            selectedPatient
        };
    }, [searchTerm, searchResults, selectedPatient]);

    useEffect(() => {
        fetchRecentCheckIns();
        fetchDepartments();
        
        // Restore check-in data if patient was selected
        if (persistentCheckInState.selectedPatient) {
            setCheckInData(prev => ({
                ...prev,
                symptoms: persistentCheckInState.selectedPatient.symptoms || ''
            }));
        }
    }, []);

    const fetchDepartments = async () => {
        try {
            const depts = await apiService.getDepartments();
            setDepartments(depts);
        } catch (error) {
            console.error('Error fetching departments:', error);
        }
    };

    const fetchRecentCheckIns = async () => {
        try {
            // Get recent patients who checked in today
            const today = new Date().toISOString().split('T')[0];
            const patients = await apiService.getPatients({ 
                organizationId: currentUser.organizationId,
                limit: 20 
            });
            
            // Filter for patients who checked in today
            const todayCheckIns = patients.filter(patient => {
                const checkInDate = new Date(patient.createdAt).toISOString().split('T')[0];
                return checkInDate === today;
            });
            
            setRecentCheckIns(todayCheckIns);
        } catch (error) {
            console.error('Error fetching recent check-ins:', error);
        }
    };

    const handleSearch = async () => {
        if (!searchTerm.trim()) {
            setSearchResults([]);
            return;
        }

        setLoading(true);
        try {
            const patients = await apiService.getPatients({ 
                organizationId: currentUser.organizationId,
                search: searchTerm 
            });
            setSearchResults(patients);
        } catch (error) {
            console.error('Error searching patients:', error);
            showAlert('Failed to search patients', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handlePatientSelect = (patient) => {
        setSelectedPatient(patient);
        setCheckInData({
            appointmentType: 'consultation',
            department: patient.department || '',
            priority: 'medium',
            notes: '',
            symptoms: patient.symptoms || ''
        });
    };

    const handleCheckInDataChange = (field, value) => {
        setCheckInData(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    const handleVitalsChange = (field, value) => {
        const newVitals = {
            ...vitals,
            [field]: value
        };
        setVitals(newVitals);
        analyzeVitals(newVitals);
    };
    
    const analyzeVitals = (vitalSigns) => {
        const alerts = [];
        let suggestedPriority = 'medium';
        
        // Blood Pressure Analysis
        const systolic = parseInt(vitalSigns.bpSystolic);
        const diastolic = parseInt(vitalSigns.bpDiastolic);
        if (systolic > 180 || diastolic > 120) {
            alerts.push('⚠️ CRITICAL: Severe Hypertension - Consider Emergency');
            suggestedPriority = 'high';
        } else if (systolic > 140 || diastolic > 90) {
            alerts.push('⚠️ Elevated Blood Pressure - Monitor closely');
            if (suggestedPriority === 'medium') suggestedPriority = 'high';
        } else if (systolic < 90 || diastolic < 60) {
            alerts.push('⚠️ Low Blood Pressure - Assess for shock');
            if (suggestedPriority === 'medium') suggestedPriority = 'high';
        }
        
        // Temperature Analysis
        const temp = parseFloat(vitalSigns.temperature);
        if (temp > 39) {
            alerts.push('🔥 HIGH FEVER - Immediate attention needed');
            suggestedPriority = 'high';
        } else if (temp > 38) {
            alerts.push('🌡️ Fever detected');
        } else if (temp < 35) {
            alerts.push('❄️ Hypothermia - Critical');
            suggestedPriority = 'high';
        }
        
        // Heart Rate Analysis
        const hr = parseInt(vitalSigns.heartRate);
        if (hr > 120) {
            alerts.push('💓 Tachycardia - Elevated heart rate');
            if (suggestedPriority === 'medium') suggestedPriority = 'high';
        } else if (hr < 50) {
            alerts.push('💓 Bradycardia - Low heart rate');
            if (suggestedPriority === 'medium') suggestedPriority = 'high';
        }
        
        if (alerts.length > 0) {
            setShowVitalsAlert(true);
            setVitalsAnalysis(alerts.join('\n'));
            setCheckInData(prev => ({ ...prev, priority: suggestedPriority }));
        } else {
            setShowVitalsAlert(false);
            setVitalsAnalysis('✅ All vitals within normal range');
        }
    };

    const handleCheckIn = async () => {
        if (!selectedPatient) return;

        try {
            // Prepare vitals data if any were recorded
            const vitalsData = (vitals.weight || vitals.bpSystolic || vitals.temperature) ? {
                weight: parseFloat(vitals.weight) || null,
                bp_systolic: parseInt(vitals.bpSystolic) || null,
                bp_diastolic: parseInt(vitals.bpDiastolic) || null,
                temperature: parseFloat(vitals.temperature) || null,
                heart_rate: parseInt(vitals.heartRate) || null,
                height: parseFloat(vitals.height) || null,
                recorded_at: new Date().toISOString(),
                recorded_by: currentUser.username
            } : null;
            
            // Step 1: Update patient details (symptoms, department, vitals)
            await apiService.updatePatient({
                patientId: selectedPatient.patientId,
                symptoms: checkInData.symptoms,
                department: checkInData.department,
                // TODO: Add initialVitals to updatePatient mutation
            });

            // Step 2: Assign queue number and set initial location (CRITICAL!)
            const queueResult = await apiService.assignQueueNumber(selectedPatient.id);
            console.log('✅ Queue assigned:', queueResult);

            // Step 3: Create appointment if needed
            if (checkInData.appointmentType !== 'walk-in') {
                await apiService.createAppointment({
                    patientId: selectedPatient.id,
                    department: checkInData.department,
                    appointmentType: checkInData.appointmentType,
                    priority: checkInData.priority,
                    notes: checkInData.notes,
                    appointmentTime: new Date().toISOString()
                });
            }

            showAlert(
                `✅ Patient checked in successfully! Queue #${queueResult.assignQueueNumber.queueNumber}`, 
                'success'
            );
            
            // Show print ticket modal
            setTicketData({
                queueNumber: queueResult.assignQueueNumber.queueNumber,
                patientName: selectedPatient.name,
                department: checkInData.department,
                nextLocation: queueResult.assignQueueNumber.location || 'Triage',
                nextRoom: 'Triage Waiting Area',
                locationAddress: '1st Floor, West Wing',
                vitals: vitalsData,
                priority: checkInData.priority,
                estimatedWait: 15
            });
            setShowPrintTicket(true);
            
            // Reset form and clear persistent cache
            setSelectedPatient(null);
            setSearchTerm('');
            setSearchResults([]);
            setCheckInData({
                appointmentType: 'consultation',
                department: '',
                priority: 'medium',
                notes: '',
                symptoms: ''
            });
            setVitals({
                weight: '',
                bpSystolic: '',
                bpDiastolic: '',
                temperature: '',
                heartRate: '',
                height: ''
            });
            
            // Clear persistent state cache
            persistentCheckInState = {
                searchTerm: '',
                searchResults: [],
                selectedPatient: null
            };
            
            // Refresh recent check-ins
            fetchRecentCheckIns();
        } catch (error) {
            console.error('Error checking in patient:', error);
            showAlert(`Failed to check in patient: ${error.message}`, 'error');
        }
    };

    const handleNewPatient = () => {
        onNavigate('add-patient');
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'high': return 'bg-red-100 text-red-800';
            case 'medium': return 'bg-yellow-100 text-yellow-800';
            case 'low': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'waiting': return 'bg-blue-100 text-blue-800';
            case 'in-progress': return 'bg-yellow-100 text-yellow-800';
            case 'completed': return 'bg-green-100 text-green-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Patient Check-in</h2>
                    <p className="text-gray-600">Register and check in patients</p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleNewPatient}
                        className="bg-green-500 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-600"
                    >
                        <UserPlus className="w-4 h-4" />
                        New Patient
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <UserPlus className="w-6 h-6 text-blue-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Today's Check-ins</p>
                            <p className="text-xl font-bold text-gray-900">{recentCheckIns.length}</p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <Clock className="w-6 h-6 text-yellow-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Waiting</p>
                            <p className="text-xl font-bold text-gray-900">
                                {recentCheckIns.filter(p => p.status === 'waiting').length}
                            </p>
                        </div>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm border">
                    <div className="flex items-center">
                        <CheckCircle className="w-6 h-6 text-green-500" />
                        <div className="ml-3">
                            <p className="text-sm font-medium text-gray-600">Completed</p>
                            <p className="text-xl font-bold text-gray-900">
                                {recentCheckIns.filter(p => p.status === 'completed').length}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Patient Search */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Search className="w-5 h-5" />
                            Find Patient
                        </h3>
                    </div>
                    <div className="p-6 min-h-[300px]">
                        <div className="flex gap-2 mb-4">
                            <input
                                type="text"
                                placeholder="Search by name, phone, or ID..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                                onClick={handleSearch}
                                disabled={loading}
                                className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50"
                            >
                                {loading ? 'Searching...' : 'Search'}
                            </button>
                        </div>

                        {/* Search Results */}
                        {searchResults.length > 0 && (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {searchResults.map((patient) => (
                                    <div 
                                        key={patient.id} 
                                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                                            selectedPatient?.id === patient.id 
                                                ? 'border-blue-500 bg-blue-50' 
                                                : 'hover:bg-gray-50'
                                        }`}
                                        onClick={() => handlePatientSelect(patient)}
                                    >
                                        <div className="flex justify-between items-start">
                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-900">{patient.name}</h4>
                                                <p className="text-sm text-gray-600">
                                                    {patient.age ? `${patient.age} years` : 'Age not specified'} • {patient.phone}
                                                </p>
                                                {patient.address && (
                                                    <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                                                        <MapPin className="w-3 h-3" />
                                                        {patient.address}
                                                    </p>
                                                )}
                                            </div>
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                                                {patient.status}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* No Results */}
                        {searchTerm && searchResults.length === 0 && !loading && (
                            <div className="text-center py-8 text-gray-500">
                                <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>No patients found matching "{searchTerm}"</p>
                                <button
                                    onClick={handleNewPatient}
                                    className="mt-2 text-blue-600 hover:text-blue-800 font-medium"
                                >
                                    Create new patient
                                </button>
                            </div>
                        )}

                        {/* Empty State - No search performed yet */}
                        {!searchTerm && searchResults.length === 0 && !loading && (
                            <div className="text-center py-8 text-gray-400">
                                <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p className="text-sm">Enter a name, phone number, or patient ID above</p>
                                <p className="text-xs mt-2">Press Enter or click Search to find patients</p>
                                <div className="mt-4 pt-4 border-t">
                                    <p className="text-sm font-medium text-gray-600 mb-2">Quick Actions</p>
                                    <button
                                        onClick={handleNewPatient}
                                        className="text-green-600 hover:text-green-800 font-medium text-sm"
                                    >
                                        + Register New Patient
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Check-in Form */}
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <FileText className="w-5 h-5" />
                            Check-in Details
                        </h3>
                    </div>
                    <div className="p-6">
                        {!selectedPatient ? (
                            <div className="text-center py-8 text-gray-500">
                                <UserPlus className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                <p>Select a patient to begin check-in process</p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Patient Info */}
                                <div className="bg-gray-50 p-4 rounded-lg">
                                    <h4 className="font-semibold text-gray-900">{selectedPatient.name}</h4>
                                    <p className="text-sm text-gray-600">
                                        {selectedPatient.age ? `${selectedPatient.age} years` : 'Age not specified'} • {selectedPatient.phone}
                                    </p>
                                    {selectedPatient.address && (
                                        <p className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                                            <MapPin className="w-3 h-3" />
                                            {selectedPatient.address}
                                        </p>
                                    )}
                                </div>

                                {/* Vital Signs Screening */}
                                <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50">
                                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                        <Activity className="w-4 h-4 text-blue-600" />
                                        Basic Vital Signs (Optional)
                                    </h5>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Weight (kg)</label>
                                            <input
                                                type="number"
                                                step="0.1"
                                                value={vitals.weight}
                                                onChange={(e) => handleVitalsChange('weight', e.target.value)}
                                                placeholder="70"
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Height (cm)</label>
                                            <input
                                                type="number"
                                                value={vitals.height}
                                                onChange={(e) => handleVitalsChange('height', e.target.value)}
                                                placeholder="170"
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Blood Pressure (mmHg)</label>
                                            <div className="flex gap-1 items-center">
                                                <input
                                                    type="number"
                                                    value={vitals.bpSystolic}
                                                    onChange={(e) => handleVitalsChange('bpSystolic', e.target.value)}
                                                    placeholder="120"
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                                <span className="text-gray-500">/</span>
                                                <input
                                                    type="number"
                                                    value={vitals.bpDiastolic}
                                                    onChange={(e) => handleVitalsChange('bpDiastolic', e.target.value)}
                                                    placeholder="80"
                                                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Heart Rate (bpm)</label>
                                            <input
                                                type="number"
                                                value={vitals.heartRate}
                                                onChange={(e) => handleVitalsChange('heartRate', e.target.value)}
                                                placeholder="75"
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                        <div className="col-span-2">
                                            <label className="block text-xs font-medium text-gray-700 mb-1">Temperature (°C)</label>
                                            <input
                                                type="number"
                                                step="0.1"
                                                value={vitals.temperature}
                                                onChange={(e) => handleVitalsChange('temperature', e.target.value)}
                                                placeholder="37.0"
                                                className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* Vitals Analysis Alert */}
                                    {vitalsAnalysis && (
                                        <div className={`mt-3 p-3 rounded-lg text-sm ${
                                            showVitalsAlert 
                                                ? 'bg-red-50 border border-red-200 text-red-800' 
                                                : 'bg-green-50 border border-green-200 text-green-800'
                                        }`}>
                                            <div className="whitespace-pre-line">{vitalsAnalysis}</div>
                                        </div>
                                    )}
                                    
                                    <p className="text-xs text-gray-500 mt-2">
                                        ℹ️ Taking vitals at reception helps prioritize patients and speeds up triage
                                    </p>
                                </div>

                                {/* Appointment Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Visit Type
                                    </label>
                                    <select
                                        value={checkInData.appointmentType}
                                        onChange={(e) => handleCheckInDataChange('appointmentType', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                        <option value="consultation">Consultation</option>
                                        <option value="follow-up">Follow-up</option>
                                        <option value="emergency">Emergency</option>
                                        <option value="walk-in">Walk-in</option>
                                        <option value="procedure">Procedure</option>
                                    </select>
                                </div>

                                {/* Department */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Department
                                    </label>
                                    <input
                                        type="text"
                                        list="departments-list"
                                        value={checkInData.department}
                                        onChange={(e) => handleCheckInDataChange('department', e.target.value)}
                                        placeholder="Type or select department..."
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                    <datalist id="departments-list">
                                        {departments.map((dept) => (
                                            <option key={dept.id} value={dept.name} />
                                        ))}
                                    </datalist>
                                    <p className="mt-1 text-xs text-gray-500">
                                        {departments.length > 0 
                                            ? 'Start typing to see suggestions from existing departments'
                                            : 'Type department name (e.g., Cardiology, Pediatrics)'
                                        }
                                    </p>
                                </div>

                                {/* Priority */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Priority Level
                                    </label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {['low', 'medium', 'high'].map((priority) => (
                                            <button
                                                key={priority}
                                                onClick={() => handleCheckInDataChange('priority', priority)}
                                                className={`p-2 rounded-lg border text-sm font-medium ${
                                                    checkInData.priority === priority 
                                                        ? getPriorityColor(priority) 
                                                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                                                }`}
                                            >
                                                {priority.charAt(0).toUpperCase() + priority.slice(1)}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Symptoms */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Chief Complaint / Symptoms
                                    </label>
                                    <textarea
                                        rows={3}
                                        placeholder="Describe the patient's main symptoms or reason for visit..."
                                        value={checkInData.symptoms}
                                        onChange={(e) => handleCheckInDataChange('symptoms', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                {/* Notes */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Additional Notes
                                    </label>
                                    <textarea
                                        rows={2}
                                        placeholder="Any additional information..."
                                        value={checkInData.notes}
                                        onChange={(e) => handleCheckInDataChange('notes', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </div>

                                {/* Check-in Button */}
                                <button
                                    onClick={handleCheckIn}
                                    className="w-full bg-green-500 text-white px-4 py-3 rounded-lg hover:bg-green-600 flex items-center justify-center gap-2 font-medium"
                                >
                                    <CheckCircle className="w-5 h-5" />
                                    Check In Patient
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Recent Check-ins */}
            {recentCheckIns.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm border">
                    <div className="p-6 border-b">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            Today's Check-ins ({recentCheckIns.length})
                        </h3>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {recentCheckIns.map((patient) => (
                                <div key={patient.id} className="border rounded-lg p-4 hover:bg-gray-50">
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-semibold text-gray-900">{patient.name}</h4>
                                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(patient.status)}`}>
                                            {patient.status}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-600">
                                        {patient.age ? `${patient.age} years` : 'Age not specified'} • {patient.phone}
                                    </p>
                                    {patient.symptoms && (
                                        <p className="text-xs text-gray-500 mt-1">
                                            {patient.symptoms.substring(0, 60)}...
                                        </p>
                                    )}
                                    <p className="text-xs text-gray-400 mt-2">
                                        Checked in: {new Date(patient.createdAt).toLocaleTimeString()}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Print Queue Ticket Modal */}
            {showPrintTicket && ticketData && (
                <PrintQueueTicket
                    {...ticketData}
                    onClose={() => {
                        setShowPrintTicket(false);
                        setTicketData(null);
                    }}
                />
            )}
        </div>
    );
};

export default ReceptionistCheckIn;
